import type {
  AIBasicModelMessage,
  AIConfig as AIConfigType,
  AIContext,
  AIContextSettings,
  AIProvider,
  ChatMessage,
} from '@tunasong/ai-lib'
import { aiConfigSchema } from '@tunasong/ai-lib'
import { AIConfigForm, AIConfigPreview } from '@tunasong/ai-ui'
import { graphHooks } from '@tunasong/graph-lib/react'
import { nlpApi, skipToken, useThunkDispatch } from '@tunasong/redux'
import { isAudio, isImage, isStorageUrlsExpired } from '@tunasong/schemas'
import { LinearProgress, useImage, usePreviewGeneration, useUserConfig } from '@tunasong/ui-lib'
import type { ModelMessage } from 'ai'
import type { FC } from 'react'
import { useState } from 'react'

interface StreamParams {
  prompt: ModelMessage[]
  model: string
  providerName: string
  requestId: string
}

export const AIConfig: FC = () => {
  const [userConfig, updateUserConfig, hasLoaded] = useUserConfig()
  const { profile } = graphHooks.useProfile()
  const dispatch = useThunkDispatch()

  // Text streaming state
  const [streamParams, setStreamParams] = useState<StreamParams | null>(null)
  const { currentData: textResult } = nlpApi.useCompleteStreamQuery(streamParams ?? skipToken)

  const imagePreview = usePreviewGeneration('image preview')
  const imageUrl = useImage(isImage(imagePreview.entity) ? imagePreview.entity : undefined, 'medium')

  const musicPreview = usePreviewGeneration('music preview')
  const musicEntity = musicPreview.entity
  const musicUrl =
    isAudio(musicEntity) && !isStorageUrlsExpired(musicEntity.storageUrls) ? musicEntity.storageUrls?.url : undefined

  const handleUpdateAIConfig = (aiConfig: AIConfigType) => updateUserConfig('ai', aiConfig)

  const handleGenerateText = (params: {
    provider: AIProvider
    model: string
    prompt: ModelMessage[]
    requestId: string
  }) =>
    setStreamParams({
      prompt: params.prompt,
      model: params.model,
      providerName: params.provider,
      requestId: params.requestId,
    })

  const handleGenerateImage = async (params: { description: string; model: string }) => {
    if (!profile) {
      return
    }
    const prompt: ChatMessage[] = [{ role: 'user', content: params.description }]
    await imagePreview.generate(() =>
      dispatch(
        nlpApi.endpoints.image.initiate({
          prompt,
          chatModel: params.model,
          parentId: profile.id,
          name: 'Style Preview',
          size: '1024x1024',
        })
      ).unwrap()
    )
  }

  const handleGenerateMusic = async (params: { description: string; model: string }) => {
    if (!profile) {
      return
    }
    const prompt: AIBasicModelMessage[] = [{ role: 'user', content: params.description }]
    await musicPreview.generate(() =>
      dispatch(
        nlpApi.endpoints.generateMusic.initiate({
          prompt,
          textModel: params.model,
          parentId: profile.id,
          name: 'Music Style Preview',
          durationSec: 15,
          instrumental: true,
        })
      ).unwrap()
    )
  }

  // Clear preview state when context changes
  const handleContextChange = () => {
    setStreamParams(null)
    imagePreview.reset()
    musicPreview.reset()
  }

  const renderPreview = ({ context, settings }: { context: AIContext; settings: AIContextSettings }) => (
    <AIConfigPreview
      key={context}
      context={context}
      settings={settings}
      textResult={streamParams ? textResult : undefined}
      imageUrl={imageUrl}
      imagePending={imagePreview.pending}
      musicUrl={musicUrl}
      musicPending={musicPreview.pending}
      onGenerateText={handleGenerateText}
      onGenerateImage={handleGenerateImage}
      onGenerateMusic={handleGenerateMusic}
      onContextChange={handleContextChange}
    />
  )

  const aiConfigResult = aiConfigSchema.safeParse(userConfig.ai)

  return hasLoaded ? (
    <AIConfigForm defaultValue={aiConfigResult.data} onChange={handleUpdateAIConfig} renderPreview={renderPreview} />
  ) : (
    <LinearProgress />
  )
}

export default AIConfig
